import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/api/billing';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { formatDate } from '@/lib/formatDate';
import { cn } from '@/lib/cn';
import { Check } from 'lucide-react';

const PLANS = [
  { id: 'free' as const, name: 'Free', price: 0, features: ['50 envios/mes', '1 workspace', 'Notificacoes por e-mail'] },
  { id: 'pro' as const, name: 'Pro', price: 29.9, features: ['5.000 envios/mes', 'Colaboracao em equipe', 'Analiticos', 'Webhooks', 'Suporte prioritario'] },
  { id: 'business' as const, name: 'Business', price: 99.9, features: ['Envios ilimitados', 'Tudo do Pro', 'Dominios personalizados', 'Analiticos avancados', 'Suporte dedicado'] },
];

export function BillingPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.billing.plan(orgId),
    queryFn: () => billingApi.getSubscription(orgId),
    enabled: !!orgId,
  });

  const subscribeMutation = useMutation({
    mutationFn: ({ plan, billingType }: { plan: 'pro' | 'business'; billingType: 'PIX' | 'BOLETO' | 'CREDIT_CARD' }) =>
      billingApi.subscribe(orgId, plan, billingType),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.billing.plan(orgId) }),
  });

  const cancelMutation = useMutation({
    mutationFn: () => billingApi.cancel(orgId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.billing.plan(orgId) }),
  });

  const sub = data?.subscription;
  const currentPlan = sub?.plan ?? 'free';
  const usage = sub?.usage ?? 0;
  const limit = sub?.limit ?? 50;
  const usagePercent = limit === Infinity ? 0 : Math.min((usage / limit) * 100, 100);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Faturamento e Plano</h1>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      ) : (
        <>
          {/* Current plan + usage */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">Plano Atual</p>
                <p className="text-xl font-bold capitalize">{sub?.planName ?? 'Free'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Periodo de Faturamento</p>
                <p className="text-sm">
                  {sub ? `${formatDate(sub.currentPeriodStart)} - ${formatDate(sub.currentPeriodEnd)}` : 'N/A'}
                </p>
              </div>
            </div>

            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-gray-600">Envios neste periodo</span>
              <span className="font-medium">{usage} / {limit === Infinity ? 'Ilimitado' : limit}</span>
            </div>
            {limit !== Infinity && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={cn('h-2 rounded-full transition-all', usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-yellow-500' : 'bg-primary')}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
            )}

            {currentPlan !== 'free' && (
              <button
                onClick={() => { if (confirm('Cancelar assinatura? Voce sera rebaixado para o plano Gratuito no final do periodo.')) cancelMutation.mutate(); }}
                disabled={cancelMutation.isPending}
                className="mt-4 text-sm text-red-500 hover:underline"
              >
                {cancelMutation.isPending ? 'Cancelando...' : 'Cancelar assinatura'}
              </button>
            )}
          </div>

          {/* Plan cards */}
          <h2 className="text-lg font-semibold mb-4">Planos</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={cn(
                  'bg-white rounded-xl border-2 p-6',
                  currentPlan === plan.id ? 'border-primary' : 'border-gray-200',
                )}
              >
                <h3 className="text-lg font-bold">{plan.name}</h3>
                <p className="text-2xl font-bold mt-2">
                  {plan.price === 0 ? 'Free' : `R$${plan.price.toFixed(2)}`}
                  {plan.price > 0 && <span className="text-sm font-normal text-gray-500">/mes</span>}
                </p>
                <ul className="mt-4 space-y-2">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {currentPlan === plan.id ? (
                  <div className="mt-6 py-2 text-center text-sm font-medium text-primary border border-primary rounded-lg">
                    Plano Atual
                  </div>
                ) : plan.id !== 'free' ? (
                  <button
                    onClick={() => subscribeMutation.mutate({ plan: plan.id as 'pro' | 'business', billingType: 'PIX' })}
                    disabled={subscribeMutation.isPending}
                    className="mt-6 w-full py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
                  >
                    {subscribeMutation.isPending ? 'Processando...' : currentPlan === 'free' ? 'Fazer upgrade' : 'Trocar'}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
